import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useState } from 'react'
import { toast } from 'react-hot-toast'

import { type Organization, OrganizationVisibility } from '@ankor-io/common/types/account'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Field, Label } from '@ankor-io/ui-components/src/Fieldset'
import { Input } from '@ankor-io/ui-components/src/Input'

import { useUserContext } from '@/providers/user/useUserContext'

type OrganizationSetupProps = {
  onSave: (orgId: string) => void
}

export const OrganizationSetup = ({ onSave }: OrganizationSetupProps) => {
  const [orgName, setOrgName] = useState('')
  const { getAccessToken } = useKindeAuth()
  const { organizations, activeOrganizationId, updateOrganization } = useUserContext()
  const [isSaving, setIsSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    const organizationDetail = activeOrganizationId ? organizations[activeOrganizationId] : null
    if (!organizationDetail) {
      toast.error('Something went wrong. No organization found.')
      setIsSaving(false)
      return
    }

    const handleUpdateOrganization = async () => {
      try {
        const organization: Organization = {
          id: organizationDetail.id,
          name: orgName.trim(),
          description: organizationDetail.description,
          visibility: OrganizationVisibility.VISIBLE,
        }

        const res = await fetch(`/api/iam/org/${activeOrganizationId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          body: JSON.stringify(organization),
        })

        if (!res.ok) {
          throw new Error('Failed to save organization name')
        }

        onSave(organizationDetail.id)
        updateOrganization(organizationDetail.id, orgName.trim(), organizationDetail.description)
      } catch (error) {
        console.error(error)
        throw new Error('Failed to save organization name')
      } finally {
        setIsSaving(false)
      }
    }

    toast.promise(handleUpdateOrganization(), {
      loading: 'Saving organization name...',
      success: 'Organization name saved',
      error: 'Failed to save organization name',
    })
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="space-y-3 sm:space-y-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-50">Let&apos;s Get Started</h1>
        <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400">
          Setup your organization name, add your vessels, and invite your team members.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-4 sm:space-y-6"
      >
        <Field>
          <Label
            htmlFor="organization-name"
            className="font-medium text-sm sm:text-base text-gray-900 dark:text-gray-50"
          >
            Organization Name
          </Label>
          <Input
            id="organization-name"
            type="text"
            placeholder="Enter your organization name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            className="disabled:cursor-not-allowed disabled:opacity-50"
            autoFocus
            disabled={isSaving}
          />
        </Field>

        <Button
          type="submit"
          color="primary"
          className="w-full disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!orgName.trim() || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </form>
    </div>
  )
}

export default OrganizationSetup
